/**
 * Category Cockpit — the action DEFINITIONS (band B).
 *
 * One function per action: it builds the `<Act>` definition — the label, the confirmation an
 * operator reads before anything is written, and how to read the reply — from the category the
 * screen is showing. The call itself lives in `@/services/categoryCockpitActions`, which is the
 * only place that knows a URL.
 *
 * ⛔ A SITE IS NEVER CHOSEN SILENTLY. Four of these endpoints take a `siteId`, and a category can
 * belong to several sites of the sector. Picking "the biggest" for the operator would run a paid
 * job against a site they never named, so those actions render ONE BUTTON PER SITE, each carrying
 * the site's name and its own product count.
 */
import React from 'react';
import * as A from '@/services/categoryCockpitActions';
import type { ActionResult } from '@/services/categoryCockpitActions';
import type { CategoryDetail, SiteCell } from '@/services/categoryCockpitService';
import type { ActionDef, Target } from '../components/Act';
import { ASPECT_LABEL, cost, fmt, leaf, plural } from './format';

const num = (v: unknown): number | null => (v == null || v === '' ? null : Number(v));

/** the sites of this category that hold products — the ones a per-site job can be run against */
/** the sector as a reader names it; the id only when the caller had no name to give */
const sectorWord = (sectorId: number, sectorName?: string) => sectorName || `sector ${sectorId}`;
/** what a category-scoped write is about — carried into the log, which outlives the screen */
const onCategory = (sectorId: number, c: { category_path: string }, sectorName?: string, site?: string | null): Target => ({
  sector: sectorWord(sectorId, sectorName), path: c.category_path, ...(site ? { site } : {}),
});

export const actionSites = (c: CategoryDetail['category']): SiteCell[] => (c.sites || []).filter((s) => s.products > 0);

// ============================================================================
// The ten aspects
// ============================================================================

/**
 * ⛔⛔ THE SECTOR REBUILD, IN ONE PLACE — EVERY ENTRY POINT THAT REACHES IT QUOTES THIS.
 *
 * Every writer of a category's personas re-runs the sector's `sector_personas` projection after its
 * own commit (services/persona/sectorPersonasSync.js @ 34aaadc21). From this cockpit that is:
 *   - Re-research personas — `refresh/:aspect` (routes/category-characteristics.js:2767), portraits ON;
 *   - Research all 10 aspects, and the inbox's research retry — `researchCharacteristics` →
 *     `_syncSectorPersonasAfterResearch` (CategoryCharacteristicsService.js:3217/3239), portraits off;
 *   - Retire a bare leaf — after the delete commits (routes/category-characteristics.js:6157), portraits off;
 *   - the concept agent actions, when the concept is a persona (CategoryCharacteristicsService.js:7467/7608/7906).
 * Round 13 disclosed it on the first entry point only, and round 14 found the next two describing
 * category-local writes (codex-reviewer + codex-reviewer-2). The disclosure therefore lives here and
 * is quoted, so an entry point cannot describe the rebuild in its own, drifting words.
 * ⚠️ Portraits are NOT part of this sentence: only `refresh/:aspect` generates them, and it adds its own
 *   cost line. A research path that claimed image spend would be wrong in the other direction.
 */
/**
 * `researchCharacteristics` DISCARDS the rebuild's return value (CategoryCharacteristicsService.js:3217
 * @ 34aaadc21) and records a failure on the category row instead, so the reply of a research run
 * cannot say whether the rebuild worked. The confirmation says where the answer is, rather than let
 * a green result imply it.
 */
export const SILENT_REBUILD_FAILURE = (
  <>If the sector rebuild fails, this reply does not say so: the failure is recorded on the category row as <code>research_metadata.sector_sync</code>, with the retry handle.</>
);

export function sectorPersonaRebuild(sectorName?: string) {
  const sector = sectorName ? <b>{sectorName}</b> : <>this sector</>;
  return {
    writes: (
      <>
        {' '}Then, once that commits, <b>the whole of {sector}&apos;s <code>sector_personas</code> is rebuilt</b> from every researched category of the sector: personas no category proposes any more are moved to <code>sector_personas_retired</code> and deleted, and the <code>characteristic_questions</code> that pointed at them are re-pointed to a successor or <b>unlinked</b>.
      </>
    ),
    reach: <><b>the whole sector</b>, not only this category: <code>sector_personas</code> is one table per sector, read by every site of {sector}.</>,
  };
}

/**
 * ⛔ A FULL RESEARCH ALSO DISCOVERS ONLINE COMMUNITIES — TWO MORE PROVIDERS AND ANOTHER TABLE.
 *
 * `researchCharacteristics` runs `_discoverCommunities` in parallel with the ten aspect prompts
 * (CategoryCharacteristicsService.js:2871 @ 891a948111). Unless the category already has
 * communities none of which is stale (CategoryCommunityDiscoveryService.js:55 @ 891a948111), that
 * calls OpenAI gpt-4o AND Gemini gemini-2.0-flash in parallel (:133) and upserts the result into
 * `category_communities` (:536). A failure there degrades to "no communities" without failing the
 * research (CategoryCharacteristicsService.js:5333 @ 891a948111). Both confirmations of that
 * endpoint said "ten model calls" and named only `category_characteristics` (codex review minor
 * on PR #6479) — one sentence, shared, so they cannot drift apart.
 */
export const COMMUNITY_DISCOVERY = {
  writes: <> It also refreshes the category&apos;s online communities in <code>category_communities</code> when it has none or one of them is stale.</>,
  cost: <> When it does, that is two more calls — OpenAI <code>gpt-4o</code> and Google <code>gemini-2.0-flash</code>, in parallel; a failure there does not fail the research.</>,
};

/**
 * POST …/research-all on one category, whichever screen offers it — the in-flight guard's key
 * (Act.tsx `op`). Sector and path are the whole identity of the call.
 */
export const researchAllOp = (sectorId: number, path: string) => `research-all:${JSON.stringify([sectorId, path])}`;

export function researchAll(sectorId: number, c: CategoryDetail['category'], sectorName?: string): ActionDef {
  const states = c.derived.states;
  const rebuild = sectorPersonaRebuild(sectorName);
  return {
    label: states.never === 10 ? 'Research this category' : 'Re-research all 10 aspects',
    target: onCategory(sectorId, c, sectorName),
    op: researchAllOp(sectorId, c.category_path),
    confirm: {
      title: 'Research all 10 aspects',
      verb: 'Research',
      danger: states.never < 10,
      writes: <>the ten aspect columns of <code>category_characteristics</code> for <b>{leaf(c.category_path)}</b>, plus an attribute-intelligence pass into <code>category_spec_cache.value_patterns</code>.{COMMUNITY_DISCOVERY.writes}{rebuild.writes}</>,
      reach: rebuild.reach,
      cost: <>ten model calls and one attribute-intelligence run, synchronously.{COMMUNITY_DISCOVERY.cost} The last measured run on this row cost <b>{cost(c.research_cost_usd as number | null)}</b>{c.sample_product_count ? <> on {plural(Number(c.sample_product_count), 'product')}</> : null}. No portraits are generated on this path.</>,
      warn: <>{states.never < 10
        ? <><b>This overwrites what is there.</b> Every aspect that currently holds a value is replaced by the new run — including curated persona benefit weights, which the reply lists as <code>benefitWeightsReplaced</code>.{' '}</>
        : null}{SILENT_REBUILD_FAILURE}</>,
    },
    run: () => A.researchAllAspects(sectorId, c.category_path),
    render: (r) => <AccountingSaid r={r} />,
  };
}

/**
 * ⛔⛔ PERSONAS IS NOT "ONE COLUMN AND ONE MODEL CALL" — IT IS THE WHOLE SECTOR, AND IT PAYS FOR IMAGES.
 *
 * `POST …/refresh/:aspect` runs, for `personas` only and AFTER the category write commits,
 * `syncSectorPersonasAfterCommit` (routes/category-characteristics.js:2766-2769 @ 34aaadc21), which
 * calls `syncSectorPersonas(sectorId, …)` WITHOUT a `portraits` option — and its default is
 * `portraits = true` (services/persona/sectorPersonasSync.js:15 @ 34aaadc21). The projection it runs,
 * `AEOOnboardingOrchestratorService._syncPersonasToSectorTable` (:3378 @ 34aaadc21):
 *   - upserts one `sector_personas` row per persona of EVERY researched category of the sector;
 *   - moves every row no category proposes any more to `sector_personas_retired` and DELETES it;
 *   - re-points `characteristic_questions.persona_id` of a retired persona to its successor, or sets
 *     it to NULL when none can be named — the wheel's question loses its persona;
 *   - then calls Leonardo (`generatePersonaImage`) once per new row without a portrait: paid image
 *     generation, a count nobody can state before the run.
 * The confirmation said "the personas column of <this leaf>" and "one model call" — an operator
 * approving that was authorising a sector-wide rebuild with image spend (codex-reviewer-2, round 13).
 * The sentences below name each of those, and `reach` overrides the category's own reach sentence,
 * which would otherwise be read as the scope of this write.
 */
function personasRefreshConfirm(c: CategoryDetail['category'], sectorName: string | undefined) {
  const rebuild = sectorPersonaRebuild(sectorName);
  return {
    writes: (
      <>
        the <code>personas</code> column of <b>{leaf(c.category_path)}</b> and a row in <code>category_characteristics_research_history</code>.{rebuild.writes}
      </>
    ),
    reach: rebuild.reach,
    cost: <>one model call for the research, <b>plus one Leonardo image generation for every sector persona that has no portrait yet</b> — paid, and how many is only known once the rebuild runs. This is the only entry point that generates portraits.</>,
    warn: (
      <>
        <b>Persona benefit weights are re-derived.</b> A persona whose key the new run does not reproduce loses its curated vector; the reply lists exactly which ones under <code>benefitWeightsReplaced</code>. A retired persona&apos;s questions may lose their persona link. If the sector rebuild fails after the category write, the reply is an error that says the category write <b>is committed</b> — it is not undone.
      </>
    ),
  };
}

export function refreshOneAspect(sectorId: number, c: CategoryDetail['category'], aspect: string, sectorName?: string): ActionDef {
  const state = String(c[`${aspect}_state`] || 'never');
  const personas = aspect === 'personas';
  return {
    label: `Re-research ${ASPECT_LABEL[aspect as keyof typeof ASPECT_LABEL] || aspect}`,
    target: onCategory(sectorId, c, sectorName),
    confirm: {
      title: `Re-research ${ASPECT_LABEL[aspect as keyof typeof ASPECT_LABEL] || aspect}`,
      verb: 'Re-research',
      // personas spends on images and retires sector rows even on a category's first research
      danger: personas || state !== 'never',
      ...(personas
        ? personasRefreshConfirm(c, sectorName)
        : {
          writes: <>the <code>{aspect}</code> column of <b>{leaf(c.category_path)}</b>, and a row in <code>category_characteristics_research_history</code>.</>,
          cost: <>one model call.</>,
          warn: state !== 'never' ? <>The current value of this aspect is replaced.</> : undefined,
        }),
    },
    // The revision the SCREEN read. The route compares it with the row it is about to overwrite and
    // refuses before paying when they differ, so a refresh fired from a stale tab costs nothing.
    run: () => A.refreshAspect(sectorId, c.category_path, aspect, (c.updated_at as string) || null),
    render: (r) => {
      const replaced = (r.body.benefitWeightsReplaced || []) as unknown[];
      return (
        <>
          <div>{String(r.body.message || 'done')}</div>
          {replaced.length ? <div className="pmk-warn pmc-small">{plural(replaced.length, 'curated benefit-weight vector')} replaced by this run.</div> : null}
          {personas ? <SectorSyncSaid s={r.body.sectorSync} /> : null}
          {r.body.updatedAt ? <div className="pmc-small pmc-mute">new revision {String(r.body.updatedAt).slice(0, 19).replace('T', ' ')}</div> : null}
        </>
      );
    },
  };
}

/**
 * What the sector rebuild actually did, from the projection's own counters (`_syncPersonasToSectorTable`
 * returns them). The confirmation could only say "how many is known once it runs"; this is where the
 * operator learns it. A reply without the field says so rather than implying a clean zero.
 */
function SectorSyncSaid({ s }: { s: unknown }) {
  if (!s || typeof s !== 'object') {
    return <div className="pmk-warn pmc-small">The reply did not report the sector rebuild, so what it retired and what it paid for is not known from here.</div>;
  }
  const n = (k: string) => Number((s as Record<string, unknown>)[k]) || 0;
  return (
    <div className="pmc-small">
      sector rebuild: {plural(n('synced'), 'persona')} current, {plural(n('retired'), 'persona')} retired, {plural(n('imagesGenerated'), 'portrait')} generated
      {n('portraitsPending') ? <>, {plural(n('portraitsPending'), 'portrait')} still pending</> : null}
      {n('questionsUnlinked') ? <span className="pmk-warn">, {plural(n('questionsUnlinked'), 'question')} left without a persona</span> : null}
      {n('questionsRepointed') ? <>, {plural(n('questionsRepointed'), 'question')} re-pointed</> : null}.
    </div>
  );
}

export function fillGaps(sectorId: number, c: CategoryDetail['category'], site: SiteCell, sectorName?: string): ActionDef {
  return {
    label: `Fill gaps from ${site.site_name}'s catalogue`,
    target: onCategory(sectorId, c, sectorName, site.site_name),
    confirm: {
      title: `Fill the research gaps from ${site.site_name}`,
      verb: 'Scan and research',
      danger: true,
      // ⛔ "THE EMPTY ONES" WAS FALSE, AND THE FALSE HALF IS THE DESTRUCTIVE ONE.
      // `CategoryCharacteristicsService.fillGapsWithData` counts product_types as a GAP when the
      // stored array holds fewer than three entries OR fails `checkTypesCoverPatterns` against the
      // catalogue's name patterns — a populated aspect qualifies on both counts. It then writes the
      // column with `_updateAspect('product_type', merge(newlyResearched, manualConcepts))`, so the
      // result is a REPLACEMENT: an existing non-manual type the new run does not reproduce is gone.
      // Only concepts marked manual are carried over by `_mergeWithManualConcepts`.
      writes: (
        <>
          the aspects of <b>{leaf(c.category_path)}</b> the scan counts as gaps, from what {site.site_name} actually sells — and <b>product types is not judged on emptiness</b>: fewer than three stored types, or types that do not cover the catalogue&apos;s name patterns, both count as a gap.
        </>
      ),
      cost: <>it reads all <b>{fmt(site.products)}</b> products of this category on {site.site_name} and then makes one model call per gap it finds.</>,
      warn: (
        <>
          <b>Product types are REPLACED, not topped up.</b> When that aspect is re-researched the column is overwritten with the newly researched types plus the concepts marked manual; an existing non-manual type the new run does not propose again <b>disappears</b>. If the stored types are curated and merely incomplete, this is a loss, not a fill.
        </>
      ),
    },
    run: () => A.fillGapsWithData(sectorId, c.category_path, site.site_id),
  };
}

export function discoverSubs(sectorId: number, c: CategoryDetail['category'], sectorName?: string): ActionDef {
  return {
    label: 'Discover sub-categories (dry run)',
    target: onCategory(sectorId, c, sectorName),
    confirm: {
      title: 'Discover sub-categories',
      verb: 'Look',
      writes: <><b>nothing.</b> This is the dry run: it reports what a split would propose and writes no proposal and no category.</>,
      cost: <>one model call over a sample of the category's products.</>,
    },
    run: () => A.discoverSubcategories(sectorId, c.category_path),
  };
}

export function markThisStale(sectorId: number, c: CategoryDetail['category'], sectorName?: string): ActionDef {
  return {
    label: 'Mark this category stale',
    target: onCategory(sectorId, c, sectorName),
    confirm: {
      title: 'Mark stale',
      verb: 'Mark stale',
      danger: true,
      writes: <><code>category_characteristics.status = &apos;stale&apos;</code> for <b>{leaf(c.category_path)}</b> only. The filter is sent explicitly: the endpoint marks the WHOLE SECTOR when it is empty.</>,
      warn: <>Enrichment and generation read the aspects whatever the status; what changes is that the row is queued for a re-research, and that this cockpit will show it as stale until one runs.</>,
    },
    run: () => A.markStale(sectorId, [c.category_path]),
    render: (r) => <div>{String(r.body.message || '')} — {((r.body.markedCategories || []) as string[]).join(', ') || 'no row matched'}</div>,
  };
}

// ============================================================================
// Schema, gaps, readiness
// ============================================================================

export function reresearchHints(sectorId: number, c: CategoryDetail['category'], site: SiteCell, sectorName?: string): ActionDef {
  const id = c.id;
  return {
    label: `Re-research extraction hints · ${site.site_name}`,
    target: onCategory(sectorId, c, sectorName, site.site_name),
    disabled: id == null ? 'the category row carries no id, which this endpoint is keyed on' : undefined,
    confirm: {
      title: `Re-research the extraction hints for ${site.site_name}`,
      verb: 'Research and re-enrich',
      danger: true,
      writes: <><code>category_spec_cache</code> rows for <b>{leaf(c.category_path)}</b> on {site.site_name} — the hints the enrichment reads to find each attribute in a product page.</>,
      cost: <><b>two jobs, not one.</b> It researches the hints AND re-enriches this category&apos;s catalogue rows against them, which may submit enrichment batch jobs and cost provider spend on up to <b>{fmt(site.products)}</b> products.</>,
      warn: (
        <>
          The run continues after the reply: the endpoint answers <code>processing</code> and does the work behind it. If it reports <code>truncated</code>, the attributes it names were never asked about — which, once in the cache, is indistinguishable from a negative result.
          {' '}
          <b>It runs on {site.site_name} or on nothing.</b> This screen offers a site on its <code>product_catalog</code> count and the endpoint verifies against <code>knowledge_base_entries</code>, so the two can disagree; when they do it answers <code>supplied_site_has_no_sample</code> and spends nothing, rather than researching and re-enriching a site you did not choose.
        </>
      ),
    },
    run: () => A.researchExtractionHints(sectorId, id, site.site_id),
    render: (r) => (
      <>
        <div>{String(r.body.message || 'started')}</div>
        {r.body.truncated ? <div className="pmk-warn pmc-small">Not asked about: {((r.body.dropped_attributes || []) as string[]).join(', ')}</div> : null}
      </>
    ),
  };
}

export function applyGapsHere(sectorId: number, c: CategoryDetail['category'], approved: number, sectorName?: string): ActionDef {
  return {
    // The count is in the label only when there IS one. "Apply 0 approved gaps" reads as a button
    // that does nothing, while the point of offering it at zero is that the zero is this cockpit's
    // reading of the queue and the endpoint is the authority on what it will accept.
    label: approved ? `Apply ${fmt(approved)} approved gaps` : 'Apply the approved gaps',
    target: onCategory(sectorId, c, sectorName),
    confirm: {
      title: 'Apply the approved gaps of this category',
      verb: 'Apply',
      writes: <>the approved mappings into <code>category_characteristics</code> for <b>{leaf(c.category_path)}</b>, and one <code>category_research_event</code> recording it. An approved <code>attribute_discovery</code> gap also writes a new row into <code>canonical_attributes</code> and queues a re-enrichment action for the category.</>,
      // ⛔ AN "ADMINISTRATIVE" WRITE THAT CALLS THE MODEL. `applyApprovedMappings` writes the
      // canonical attribute and then calls `CategorySpecCacheService.researchCategorySpecs(...)`
      // for it (backend-merged/services/SemanticGapService.js:3956 @ 52b41e15b). It is best-effort
      // and its failure does not fail the apply — which is exactly why nothing on this screen
      // showed it, and why a button described as "apply the approved mappings" was spending
      // provider budget with no cost line at all.
      cost: <>none for a plain mapping. <b>An approved <code>attribute_discovery</code> gap costs one research call per new attribute</b> — the apply researches the spec cache for it right after writing it. The call is best-effort: it can fail without failing the apply, so a spend here is not proof of a cached result.</>,
      reach: <><code>canonical_attributes</code> and the spec cache are the category's schema, not this sector's copy of it — a discovery applied here is read by every site that carries the category.</>,
      warn: <>Apply refuses rows whose suggester is unknown or a small model, so {approved ? <>fewer than {fmt(approved)} may go through</> : <>the count this screen reads is zero and the endpoint may still find rows</>} — the reply says how many did. The products do not carry the new values until a re-enrichment runs.</>,
    },
    run: () => A.applyGapsForCategory(sectorId, c.category_path),
    render: (r) => <AppliedSaid r={r} />,
  };
}

export function scoreSite(site: SiteCell): ActionDef {
  const never = !site.tier;
  return {
    label: `${never ? 'Score' : 'Re-score'} ${site.site_name}`,
    // the endpoint re-scores the WHOLE site, so the site is the target — not the category it was
    // pressed from; the same write from two categories is one write, and is held as one
    target: { site: site.site_name },
    confirm: {
      title: `Score the readiness of ${site.site_name} now`,
      verb: 'Score',
      writes: <><code>category_readiness_scores</code> for every category of {site.site_name}, a snapshot in <code>category_readiness_score_history</code>, and a refresh of the customer-priority view.</>,
      cost: <>no model call: it reads the catalogue and the knowledge and computes L1 / L3 / L5.</>,
      // a first score is not guaranteed to produce THIS row: say so before, not after
      ...(never ? { warn: <>{site.site_name} has no readiness row for this category yet. The run re-scores the whole site; this category gets a row only if the scorer includes it (it scores the (site, category) pairs that have products — {fmt(site.products)} here). The chapter re-reads afterwards and shows whether it did.</> } : {}),
    },
    run: () => A.scoreReadiness(site.site_id),
    render: (r) => <div>{fmt(num(r.body.scored))} categories scored, {fmt(num(r.body.failed))} failed.</div>,
  };
}

// ============================================================================
// Leaf retirement — offered only for a BARE leaf
// ============================================================================

export const isBareLeaf = (path: string): boolean => !path.includes(' > ');

/** what the preview said about THIS row: its verdict, and the state the route can check it against */
export interface LeafPreview {
  verdict: string;
  /** the row that SURVIVES — this is a delete, and the twin is where the content goes */
  full_path: string | null;
  /** leaf-only personas: accepted loss, enumerated by the route with the reason for each */
  discarded_personas: Array<{ persona_key: string; reason: string }>;
  /** object fields present on both rows with different content: the leaf's side is DROPPED */
  discarded_fields: string[];
  /** fields that really are merged into the twin */
  merged_fields: string[];
  expected: A.LeafExpectation | null;
}

export function retireThisLeaf(sectorId: number, c: CategoryDetail['category'], preview: LeafPreview | null, sectorName?: string): ActionDef {
  const rebuild = sectorPersonaRebuild(sectorName);
  const verdict = preview?.verdict || null;
  const expected = preview?.expected || null;
  const lost = preview?.discarded_personas || [];
  const fieldsLost = preview?.discarded_fields || [];
  const merged = preview?.merged_fields || [];
  return {
    label: 'Retire this bare-leaf row',
    target: onCategory(sectorId, c, sectorName),
    disabled: verdict !== 'DELETABLE'
      ? `the preview says ${verdict || 'nothing yet'} — only a DELETABLE row can be retired`
      // A DELETABLE verdict whose row carries no digest cannot be checked, and an unchecked delete
      // is the thing this button is not allowed to be.
      : !expected ? 'the preview row carries no approval digest, so the delete could not be checked against what you were shown'
        : undefined,
    confirm: {
      title: 'Retire the bare-leaf duplicate',
      verb: 'Retire',
      danger: true,
      // ⛔ NAME THE SURVIVOR AND NAME THE LOSS. "merged into its full-path twin" described the
      // happy half of the handler and left out the other: an object field present on BOTH rows
      // with different content is NOT merged — the twin's value stands and the leaf's is dropped
      // (CEO ruling 2026-09-09, enumerated by the route as `merge.discarded_fields`). The preview
      // knows all of it and the screen was throwing it away (codex-reviewer, round 11).
      writes: (
        <>
          this row is deleted and its content goes to <b>{preview?.full_path || 'its full-path twin'}</b>, which is the row that survives.
          {merged.length ? <> Merged into it: {merged.join(', ')}.</> : null}
          {' '}The delete and the merge are one transaction.{rebuild.writes}
        </>
      ),
      // the delete is one row; the rebuild that follows it is the sector's (routes/category-characteristics.js:6157)
      reach: rebuild.reach,
      warn: (
        <>
          {lost.length || fieldsLost.length ? (
            <>
              <b>This loses:</b>
              {lost.length ? <> {plural(lost.length, 'persona')} that exist only here — {lost.map((p) => `${p.persona_key} (${p.reason.replace(/_/g, ' ')})`).join(', ')} — recorded on the twin as discarded, not carried over.</> : null}
              {fieldsLost.length ? <> And {plural(fieldsLost.length, 'field')} both rows hold differently, where the twin&apos;s value stands and this row&apos;s is DROPPED: {fieldsLost.join(', ')}.</> : null}
              {' '}
            </>
          ) : <><b>The preview reports nothing lost:</b> no leaf-only personas and no conflicting fields.{' '}</>}
          This is duplicate cleanup, not category deletion: the endpoint refuses any path containing &quot;&gt;&quot;, and refuses a leaf with no twin.
          {' '}
          If the sector rebuild fails after the delete, the retirement stays done and the result says so, with the retry.
          {' '}
          <b>The delete is checked against the preview you just read</b> — the route recomputes the digest under the row locks and answers <code>412</code> without writing if either row has changed since. If it does, re-read the preview: what you approved is no longer what is there.
        </>
      ),
    },
    run: () => A.retireLeaf(sectorId, c.category_path, expected as A.LeafExpectation),
  };
}

// ============================================================================
// The reply readers that more than one action shares
// ============================================================================

/**
 * `POST …/refresh` reports its spend in `accounting`, and `persist_failed` there means the money
 * was spent and the row does not carry it. The route answers 200 for that, so nothing else on the
 * screen would say so.
 */
/**
 * What the per-category Apply actually did.
 *
 * ⛔⛔ THE ROUTE'S MESSAGE IS NOT THE RESULT. `POST /api/semantic-gaps/apply` answers
 * `{ success: true, message: 'Applied N mappings to category_characteristics', ...result }`, and
 * `SemanticGapService.applyApprovedMappings` can return `{ applied: 0, failed: 12 }` — twelve gaps
 * attempted, twelve gaps not written, and a 200 whose message reads "Applied 0 mappings". Printing
 * the message alone put "done" over that, with the failures a collapsed `<details>` away.
 *
 * The three skip counters are a SEPARATE fact from `failed` and the service is explicit about it:
 * skipped gaps were never attempted (a denylisted attribute, an unevaluable value, a category the
 * site's products no longer carry), so `applied + failed` describes the attempt and the skips
 * describe what was refused before it. They are shown apart for the same reason.
 *
 * ⛔⛔ AND `failed` DOES NOT MEAN "NOT WRITTEN". A site's gaps resolve to one or more category
 * paths, and each path is its OWN transaction: `_withCanonicalTxn` runs once per `writePath`, and a
 * group is counted applied only after the LAST path commits — which is also the only path that marks
 * the gaps (SemanticGapService.js:3789-3806 @ 34aaadc21). Path 1 committed, path 2 threw: the reply
 * says `applied: 0, failed: 1`, the gap stays queued, and path 1's knowledge change is written. This
 * said "Nothing was written" over exactly that (codex-reviewer, round 18). "Nothing was written" is
 * now reserved for the one case that proves it — no attempt at all, only skips.
 */
function AppliedSaid({ r }: { r: ActionResult }) {
  const n = (v: unknown) => (typeof v === 'number' ? v : Number(v || 0));
  const applied = n(r.body.applied);
  const failed = n(r.body.failed);
  const denied = n(r.body.skippedDenylisted);
  const unevaluable = n(r.body.skippedUnevaluable);
  const unresolvable = n(r.body.skippedUnresolvable);
  const skipped = denied + unevaluable + unresolvable;
  const deniedAttrs = Array.isArray(r.body.skippedDenylistedAttributes) ? (r.body.skippedDenylistedAttributes as unknown[]).map(String) : [];
  const unresolvableErrors = Array.isArray(r.body.unresolvableCategoryErrors) ? (r.body.unresolvableCategoryErrors as unknown[]) : [];
  const clean = failed === 0 && skipped === 0;
  return (
    <>
      <div className={clean ? undefined : 'pmk-warn'}>
        <b>{fmt(applied)}</b> applied{failed ? <>, <b>{fmt(failed)}</b> FAILED</> : null}{skipped ? <>, {fmt(skipped)} never attempted</> : null}.
        {applied === 0 && !failed && skipped ? ' Nothing was written: every gap was refused before an attempt.' : ''}
      </div>
      {failed ? (
        <div className="pmc-small pmk-warn">
          <b>{plural(failed, 'approved mapping')} did not complete</b>, and may be <b>partly written</b>: Apply commits each category path of a site separately, so a failure on a later path leaves the earlier paths already written. The gaps stay queued, so the screen still counts them as open. The route answers 200 for this, so nothing else on the screen would say so; the per-gap reasons are in the server log.
        </div>
      ) : null}
      {denied ? (
        <div className="pmc-small pmc-mute">
          {plural(denied, 'gap')} refused by the attribute denylist{deniedAttrs.length ? `: ${deniedAttrs.slice(0, 10).join(', ')}${deniedAttrs.length > 10 ? ' …' : ''}` : ''} — a policy decision, not a failure.
        </div>
      ) : null}
      {unevaluable ? <div className="pmc-small pmc-mute">{plural(unevaluable, 'gap')} had no evaluable value.</div> : null}
      {unresolvable ? (
        <div className="pmc-small pmc-mute">
          {plural(unresolvable, 'gap')} could not be resolved to a write key — the site&apos;s products no longer carry the category
          {unresolvableErrors.length ? `: ${unresolvableErrors.map((e) => (e && typeof e === 'object' ? String((e as Record<string, unknown>).error || JSON.stringify(e)) : String(e))).slice(0, 3).join(' · ')}` : ''}.
        </div>
      ) : null}
    </>
  );
}

/**
 * Exported because the SAME endpoint is reachable from two screens. The inbox's research-retry
 * answers the identical envelope, and leaving it on `ServerSaid` printed the route's fixed
 * "Research completed successfully" over a failed paid run — the defect this component exists to
 * prevent, surviving on the one screen that did not import it (both reviewers, round 4).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function AccountingSaid({ r }: { r: ActionResult }) {
  const acc = (r.body.accounting || {}) as Record<string, unknown>;
  const status = String(acc.status || '');
  const researchError = acc.research_error ? String(acc.research_error) : '';
  const failures = Array.isArray(acc.attribute_failures) ? (acc.attribute_failures as unknown[]) : [];
  /**
   * ⛔⛔ `status` IS ABOUT THE MONEY, NOT ABOUT THE RESEARCH, AND READING IT AS BOTH HID A PAID
   * FAILURE BEHIND A GREEN LINE. The route sets
   *   `accounting.status = aiCost.calls > 0 ? 'recorded' : (research_error ? 'research_failed' : 'no_calls')`
   * so a run that CALLED THE MODEL and then reported failure lands on `recorded` with
   * `research_error` set beside it — the spend is recorded, the research did not work, and the
   * screen said "Research completed successfully" with nothing else. `attribute_failures` is the
   * same story one level down: per-attribute failures travel on every reply, including the ones
   * whose overall status is clean, and a partial failure that is never shown is a category the
   * operator believes is researched.
   * ⇒ The verdict is the UNION: the status, the error, and the per-attribute list, each read for
   *   what it actually reports.
   */
  const bad = status === 'persist_failed' || status === 'research_failed' || !!researchError || failures.length > 0;
  const names = failures
    .map((f) => (f && typeof f === 'object' ? String((f as Record<string, unknown>).attribute_name || (f as Record<string, unknown>).attribute || '') : String(f)))
    .filter(Boolean);
  return (
    <>
      {/* The route's message is fixed prose — it says "Research completed successfully" for a
          failed attribute-intelligence run too — so it is never the last word on this screen. */}
      <div className={bad ? 'pmk-warn' : undefined}>
        {bad ? 'The research did not complete cleanly.' : String(r.body.message || 'research completed')}
      </div>
      {researchError ? (
        <div className="pmc-small pmk-warn">
          attribute intelligence FAILED — {researchError}
          {status === 'recorded' ? ' · the calls were made and the spend IS on the row, so this cost money and produced nothing' : ''}
        </div>
      ) : null}
      {failures.length ? (
        <div className="pmc-small pmk-warn">
          {plural(failures.length, 'attribute')} failed inside the run{names.length ? `: ${names.slice(0, 12).join(', ')}${names.length > 12 ? ` … and ${names.length - 12} more` : ''}` : ''} — they have no interpretation, which downstream is indistinguishable from a negative result.
        </div>
      ) : null}
      {status ? (
        <div className={`pmc-small ${status === 'persist_failed' ? 'pmk-warn' : 'pmc-mute'}`}>
          spend: {status.replace(/_/g, ' ')}
          {acc.error ? ` — ${String(acc.error)}` : ''}
          {status === 'persist_failed' ? ' · the spend happened and the row does not carry it; the measured record is in the server log' : ''}
        </div>
      ) : null}
    </>
  );
}

// ============================================================================
// One gap, and one knowledge note
// ============================================================================

/**
 * `PUT /api/semantic-gaps/:id/review`.
 *
 * ⛔ THE ROUTE TAKES THREE DECISIONS AND "needs expert" IS NOT ONE OF THEM. `reviewGap` validates
 * against `approve | modify | reject` and throws on anything else (SemanticGapService, at
 * `origin/next`), so the plan's "approve / reject / needs expert" is two of three: the queue's
 * needs_expert rows are set by the verifier, not by this route. `modify` is offered instead, and
 * it is the one that requires a value — the route refuses it without one.
 */
/**
 * ⛔⛔ WHAT A GAP ROW WILL WRITE, AND WHICH DECISIONS ARE SAFE ON IT — ONE RULE FOR THE CELL AND THE BUTTONS.
 *
 * Three facts of `SemanticGapService` (@ 34aaadc21), and the screen contradicted all three:
 *   - `modify` stores the operator's value in `final_semantic_value` and leaves the suggestion alone (:3419-3424);
 *   - Apply writes `final_semantic_value`, for `approved` AND `modified` rows (:3571-3578, :3689);
 *   - `approve` sets `final_semantic_value = suggested_semantic_value` (:3416-3417) — whatever was there.
 * So after correcting A → B the table showed A, Apply wrote an unseen B, and the Approve button still
 * on the row put A back and discarded the correction without a word (codex-reviewer; raised five
 * times as minor, blocking at 32fd7b156). The cell now shows the value Apply will write, says when it
 * is a correction, and a corrected row does not offer Approve: re-approving IS reverting.
 */
export function gapView(g: { status: string; gap_type?: string | null; suggested_semantic_value: string | null; final_semantic_value?: string | null }) {
  // ⛔ ONLY A VALUE MAPPING WRITES A VALUE. Apply splits by gap_type (SemanticGapService.js:3689-3691
  // @ 34aaadc21): `value_mapping` writes `final_semantic_value`; `attribute_research` and
  // `attribute_discovery` write `suggested_research_data` — a researched attribute definition this
  // screen does not hold. Offering Correct on those rows stored a value Apply never reads: the
  // operator was told the correction succeeded and the original proposal was applied anyway
  // (codex-reviewer, round 17). `gap_type` absent is a value mapping, as Apply itself reads it.
  const mapping = (g.gap_type || 'value_mapping') === 'value_mapping';
  const reviewed = g.status === 'approved' || g.status === 'modified';
  const writes = mapping && reviewed ? (g.final_semantic_value ?? null) : null;
  const corrected = mapping && g.status === 'modified' && writes !== null && writes !== g.suggested_semantic_value;
  const decisions: Array<'approve' | 'modify' | 'reject'> = !mapping ? ['approve', 'reject']
    : g.status === 'modified' ? ['modify', 'reject'] : ['approve', 'modify', 'reject'];
  return {
    mapping,
    /** the value on screen: what Apply will write once reviewed, the suggestion before that; null when Apply writes no value */
    shown: !mapping ? null : reviewed ? writes : g.suggested_semantic_value,
    /** true when Apply will write something other than the model's suggestion */
    corrected,
    decisions,
    /** the current value a correction replaces */
    current: !mapping ? null : reviewed ? writes : g.suggested_semantic_value,
  };
}

export function reviewOneGap(gapId: string, decision: 'approve' | 'modify' | 'reject', attr: string, suggested: string | null, current?: string | null, gapType?: string | null, where?: Omit<Target, 'item'>): ActionDef {
  const mapping = (gapType || 'value_mapping') === 'value_mapping';
  const WORD = { approve: 'Approve', modify: 'Correct', reject: 'Reject' } as const;
  // the value on the row now — a correction replaces THIS, which is not always the suggestion
  const now = current === undefined ? suggested : current;
  return {
    label: WORD[decision],
    target: { ...where, item: `gap ${gapId}` },
    confirm: {
      title: `${WORD[decision]} the mapping for ${attr}`,
      verb: WORD[decision],
      danger: decision === 'reject',
      // NOT the category's reach. Reviewing a gap moves one queue row; nothing enters the knowledge
      // until Apply runs, and showing the category blast here would attribute Apply's reach to a
      // decision that has none of it.
      reach: <>one row of <code>semantic_gap_queue</code>. The knowledge is untouched until Apply runs.</>,
      writes: decision === 'approve' && !mapping
        ? <><code>semantic_gap_queue</code> row → <b>approved</b>. For a <code>{gapType}</code> row, what Apply writes is the researched attribute definition stored with the proposal (<code>suggested_research_data</code>) — not a value, and not something this screen shows. Approved is not applied: the Apply button on this chapter writes it.</>
        : decision === 'approve'
        ? <><code>semantic_gap_queue</code> row → <b>approved</b>, keeping the suggested value {suggested ? <><code>{suggested}</code></> : <span className="pmc-mute">(there is no suggestion on this row — approving keeps a null)</span>}. Approved is not applied: the Apply button on this chapter writes it into the knowledge.</>
        : decision === 'modify'
          ? <>the row → <b>modified</b>, with the value you give below. It replaces {now ? <>the current value <code>{now}</code></> : <>the row&apos;s empty value</>}{suggested && now !== suggested ? <> (the model suggested <code>{suggested}</code>)</> : null}, and it is what Apply will write — Apply takes modified rows as it takes approved ones.</>
          : <>the row → <b>rejected</b>. It leaves every open count and Apply will never write it.</>,
      ask: decision === 'modify' ? { label: 'The value the attribute should map to', placeholder: now || 'the corrected semantic value', min: 1 } : undefined,
    },
    run: (v) => A.reviewGap(gapId, decision, undefined, decision === 'modify' ? v : undefined),
  };
}

/** `POST /api/domain-knowledge/:id/approve` and `/deprecate` — one note of this category. */
export function noteAction(id: string, title: string, kind: 'approve' | 'deprecate', where?: Omit<Target, 'item'>): ActionDef {
  return {
    label: kind === 'approve' ? 'Approve' : 'Deprecate',
    target: { ...where, item: `note ${id}` },
    confirm: {
      title: `${kind === 'approve' ? 'Approve' : 'Deprecate'} "${title}"`,
      verb: kind === 'approve' ? 'Approve' : 'Deprecate',
      danger: kind === 'deprecate',
      reach: <>one <code>domain_knowledge</code> row, read by the generation for this category on every site that uses it.</>,
      writes: kind === 'approve'
        ? <>the <code>domain_knowledge</code> row becomes approved. <b>And a second write follows it:</b> approving resolves the content-improvement issues linked to this note (<code>_autoResolveLinkedIssues</code>), which is a separate pass over other rows.</>
        : <>the row becomes deprecated and the generation stops reading it. The text is kept.</>,
      warn: kind === 'approve'
        ? <>Those two writes fail independently. The route answers <code>success: true</code> as soon as the approval commits, and reports the linked-issue pass beside it in <code>postApproval</code> — so a reply that says the note was approved can also be saying that resolving its issues failed.</>
        : undefined,
      ask: kind === 'deprecate' ? { label: 'Why is it deprecated?', placeholder: 'the route requires a reason', min: 3 } : undefined,
    },
    run: (reason) => (kind === 'approve' ? A.approveKnowledgeNote(id) : A.deprecateKnowledgeNote(id, reason)),
    render: kind === 'approve' ? (r) => <ApprovalSaid r={r} /> : undefined,
  };
}

/**
 * ⛔ TWO WRITES, ONE ENVELOPE — AND THE DEFAULT RENDERER SHOWED ONLY THE FIRST.
 * `DomainKnowledgeService.updateStatus` commits the status change and then awaits
 * `_autoResolveLinkedIssues`, returning `{ success: true, postApproval: { linked, resolved,
 * failed, errorCodes }, data }` whatever that second pass did
 * (backend-merged/services/DomainKnowledgeService.js:415-423 @ 52b41e15b). `success: true` is a
 * true statement about the approval and says nothing about the issues, so the green "done" was
 * printed over failures that sat in the collapsed JSON. The two outcomes are shown apart.
 */
export function ApprovalSaid({ r }: { r: ActionResult }) {
  const pa = (r.body.postApproval || null) as Record<string, unknown> | null;
  const failed = num(pa?.failed);
  const codes = Array.isArray(pa?.errorCodes) ? (pa!.errorCodes as unknown[]).map(String) : [];
  return (
    <>
      <div>The note is approved — the generation for this category reads it from now on.</div>
      {!pa ? (
        <div className="pmc-small pmc-mute">The reply carried no <code>postApproval</code>, so the linked-issue pass did not report — nothing here says it ran.</div>
      ) : failed > 0 ? (
        <div className="pmk-warn" style={{ marginTop: 4 }}>
          But resolving the issues linked to it FAILED for {fmt(failed)} of {fmt(num(pa.linked))}{codes.length ? <> ({codes.join(', ')})</> : null}. The approval stands; those issues are still open and this button will not retry them.
        </div>
      ) : (
        <div className="pmc-small pmc-mute">{fmt(num(pa.resolved))} of {fmt(num(pa.linked))} linked issues resolved with it.</div>
      )}
    </>
  );
}
